import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, ExternalLink, RefreshCw, CheckCircle, Clock, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const colors = {
    INCOMPLETE: 'bg-yellow-100 text-yellow-800',
    READY_FOR_OPINION: 'bg-green-100 text-green-800',
    OPENED_BY_ADVOCATE: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-gray-100 text-gray-800'
  };
  
  const labels = {
    INCOMPLETE: 'Incomplete',
    READY_FOR_OPINION: 'Ready',
    OPENED_BY_ADVOCATE: 'In Progress',
    COMPLETED: 'Completed'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100'}`}>
      {labels[status] || status}
    </span>
  );
};

const AdvocateQueue = ({ onOpenCase }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/missing-docs/cases/ready-for-opinion`);
      setCases(data.cases || []);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const handleOpenCase = async (caseId) => {
    setActionLoading(caseId);
    try {
      await axios.post(`${API_URL}/api/missing-docs/case/${caseId}/open`);
      // Navigate to case detail page
      navigate(`/case/${caseId}`);
    } catch (err) {
      // Even if marking as opened fails, still navigate to view the case
      console.error('Failed to mark case as opened:', err);
      navigate(`/case/${caseId}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadZip = async (caseId) => {
    setActionLoading(caseId + '-zip');
    try {
      const response = await axios.get(
        `${API_URL}/api/missing-docs/case/${caseId}/download-zip`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${caseId}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading cases...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchCases}
          className="mt-2 text-blue-600 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No cases ready for opinion</p>
        <p className="text-sm text-gray-400">Cases will appear here when documents are complete</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="advocate-queue">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-700">
          {cases.length} case{cases.length !== 1 ? 's' : ''} ready for opinion
        </h4>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate('/case-history')}
            className="text-sm text-gray-600 hover:text-blue-600 hover:underline"
          >
            View All History
          </button>
          <button 
            onClick={fetchCases}
            className="text-sm text-blue-600 hover:underline flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {cases.map((c) => (
          <div 
            key={c.case_id} 
            className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
            data-testid={`case-${c.case_id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <h5 className="font-medium text-gray-900 truncate">
                    {c.case_name || c.case_id}
                  </h5>
                  <StatusBadge status={c.status} />
                </div>
                
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDate(c.marked_ready_at || c.created_at)}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 rounded">
                    {c.profile}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 rounded">
                    {c.property_type?.replace('_', ' ')}
                  </span>
                  <span className="text-green-600 font-medium">
                    {c.completeness_percent}% complete
                  </span>
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  <span>{c.uploaded_docs?.length || 0} documents</span>
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleDownloadZip(c.case_id)}
                  disabled={actionLoading === c.case_id + '-zip'}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 flex items-center"
                  title="Download all documents as ZIP"
                >
                  {actionLoading === c.case_id + '-zip' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-1" />
                      ZIP
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleOpenCase(c.case_id)}
                  disabled={actionLoading === c.case_id}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  title="Open case for opinion"
                >
                  {actionLoading === c.case_id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdvocateQueue;
